import type { CardPointeCnpTokenSaleRequestDetails } from './cardpointe/cardpointe-cnp-token-sale.types';
import type { CardPointeLifecycleFinalizationRequestPayload } from './cardpointe/cardpointe-lifecycle-finalization.types';
import type { StoredCardPointeLifecyclePreflightRequestPayload } from './cardpointe/cardpointe-lifecycle-preflight.types';
import type { CardPointeReconciliationLookupRequestDetails } from './cardpointe/cardpointe-reconciliation.types';
import type { CardPointeRefundRequestDetails } from './cardpointe/cardpointe-refund.types';
import type { StoredCardPointeStoredCardSaleRequestDetails } from './cardpointe/cardpointe-stored-card-sale.types';
import type {
    CardPointeTerminalSaleRequestLifecyclePayloadV2,
    StoredCardPointeTerminalSaleRequestLifecyclePayloadV1,
} from './cardpointe/cardpointe-terminal-sale-lifecycle.types';
import type { CardPointeTerminalTokenizationRequestDetails } from './cardpointe/cardpointe-terminal-tokenization.types';
import type { CardPointeTransactionStatusRequestDetails } from './cardpointe/cardpointe-transaction-status.types';
import type { CardPointeTransactionDetailsRequestDetails } from './cardpointe/cardpointe-transaction-details.types';
import type { CardPointeVoidRequestDetails } from './cardpointe/cardpointe-void.types';
import type { CloverCnpTokenSaleRequestDetails } from './clover/clover-cnp-token-sale.types';
import type { CloverLifecycleFinalizationRequestPayload } from './clover/clover-lifecycle-finalization.types';
import type { CloverLifecyclePreflightRequestPayload } from './clover/clover-lifecycle-preflight.types';
import type { CloverReconciliationLookupRequestDetails } from './clover/clover-reconciliation.types';
import type { CloverRefundRequestDetails } from './clover/clover-refund.types';
import type { CloverStoredCardSaleRequestDetails } from './clover/clover-stored-card-sale.types';
import type { CloverTerminalSaleRequestLifecyclePayload } from './clover/clover-terminal-sale-lifecycle.types';
import type { CloverTerminalTokenizationRequestDetails } from './clover/clover-terminal-tokenization.types';
import type { CloverTransactionStatusRequestDetails } from './clover/clover-transaction-status.types';
import type { CloverVoidRequestDetails } from './clover/clover-void.types';
import type { JsonObject, JsonValue } from '../../common.types';
import type { LosslessNumber } from '../../lossless-json-codec';
import type { CardType } from '../../samba-payment.types';

export const GatewayLifecycleRequestType = {
    BIN_LOOKUP: 'bin_lookup_request_v1',
    ACH_PAYMENT: 'ach_payment_request_v1',
    CARDPOINTE_CNP_TOKEN_SALE: 'cardpointe_auth_request_v1',
    CARDPOINTE_CNP_TOKEN_SALE_V2: 'cardpointe_auth_request_v2',
    CARDPOINTE_FUNDING_V2: 'cardpointe_funding_request_v2',
    CARDPOINTE_INQUIRE: 'cardpointe_inquire_request_v1',
    CARDPOINTE_INQUIRE_V2: 'cardpointe_inquire_request_v2',
    CARDPOINTE_LIFECYCLE_FINALIZATION: 'cardpointe_lifecycle_finalization_request_v1',
    CARDPOINTE_LIFECYCLE_PREFLIGHT: 'cardpointe_lifecycle_preflight_request_v1',
    CARDPOINTE_RECONCILIATION: 'cardpointe_reconciliation_request_v1',
    CARDPOINTE_REFUND: 'cardpointe_refund_request_v1',
    CARDPOINTE_REFUND_V2: 'cardpointe_refund_request_v2',
    CARDPOINTE_STORED_CARD_SALE_V1: 'cardpointe_stored_card_sale_request_v1',
    CARDPOINTE_STORED_CARD_SALE_V2: 'cardpointe_stored_card_sale_request_v2',
    CARDPOINTE_STORED_CARD_SALE_V3: 'cardpointe_stored_card_sale_request_v3',
    CARDPOINTE_TERMINAL_SALE_V1: 'cardpointe_terminal_sale_request_v1',
    CARDPOINTE_TERMINAL_SALE_V2: 'cardpointe_terminal_sale_request_v2',
    CARDPOINTE_TERMINAL_SALE_V3: 'cardpointe_terminal_sale_request_v3',
    CARDPOINTE_TERMINAL_TOKENIZATION: 'cardpointe_terminal_tokenization_request_v1',
    CARDPOINTE_TERMINAL_TOKENIZATION_V2: 'cardpointe_terminal_tokenization_request_v2',
    CARDPOINTE_TERMINAL_CANCEL_V2: 'cardpointe_terminal_cancel_request_v2',
    CARDPOINTE_TRANSACTION_STATUS: 'cardpointe_inquire_by_orderid_request_v1',
    CARDPOINTE_TRANSACTION_STATUS_V2: 'cardpointe_inquire_by_orderid_request_v2',
    CARDPOINTE_VOID: 'cardpointe_void_request_v1',
    CARDPOINTE_VOID_V2: 'cardpointe_void_request_v2',
    CLOVER_CNP_TOKEN_SALE: 'clover_charge_request_v1',
    CLOVER_CNP_TOKEN_SALE_V2: 'clover_charge_request_v2',
    CLOVER_BROWSER_TOKENIZATION: 'clover_browser_tokenization_request_v1',
    CLOVER_LIFECYCLE_FINALIZATION: 'clover_lifecycle_finalization_request_v1',
    CLOVER_LIFECYCLE_PREFLIGHT: 'clover_lifecycle_preflight_request_v1',
    CLOVER_RECONCILIATION: 'clover_reconciliation_request_v1',
    CLOVER_RECONCILIATION_V2: 'clover_reconciliation_request_v2',
    CLOVER_REFUND: 'clover_refund_request_v1',
    CLOVER_REFUND_V2: 'clover_refund_request_v2',
    CLOVER_STORED_CARD_SALE: 'clover_stored_card_sale_request_v1',
    CLOVER_STORED_CARD_SALE_V2: 'clover_stored_card_sale_request_v2',
    CLOVER_TERMINAL_SALE: 'clover_terminal_sale_request_v1',
    CLOVER_TERMINAL_SALE_V2: 'clover_terminal_sale_request_v2',
    CLOVER_TERMINAL_CANCEL: 'clover_terminal_cancel_request_v1',
    CLOVER_TERMINAL_TOKENIZATION: 'clover_terminal_tokenization_request_v1',
    CLOVER_TERMINAL_TOKENIZATION_V2: 'clover_terminal_tokenization_request_v2',
    CLOVER_TRANSACTION_DETAILS: 'clover_transaction_details_request_v1',
    CLOVER_TRANSACTION_STATUS: 'clover_payment_external_request_v1',
    CLOVER_TRANSACTION_STATUS_V2: 'clover_payment_external_request_v2',
    CLOVER_WEBHOOK_PAYMENT_RETRIEVAL: 'clover_webhook_payment_retrieval_request_v1',
    CLOVER_VOID: 'clover_void_request_v1',
    CLOVER_VOID_V2: 'clover_void_request_v2',
    INTEGRATION_RECONCILIATION: 'integration_reconcile_request_v1',
    TERMINAL_SALE_DISPATCH: 'terminal_sale_dispatch_request',
    VALOR_CARD_TOKEN_SALE: 'valor_sale_copy_request_v1',
    VALOR_CARD_TOKEN_SALE_V2: 'valor_sale_copy_request_v2',
    VALOR_BROWSER_TOKENIZATION: 'valor_browser_tokenization_request_v1',
    VALOR_RECONCILIATION: 'valor_reconcile_request_v1',
    VALOR_RECONCILIATION_V2: 'valor_reconcile_request_v2',
    VALOR_REFUND: 'valor_refund_offset_sale_v1',
    VALOR_REFUND_V2: 'valor_refund_request_v2',
    VALOR_TERMINAL_PUBLISH_V1: 'valor_vc_publish_request_v1',
    VALOR_TERMINAL_PUBLISH_V2: 'valor_vc_publish_request_v2',
    VALOR_TERMINAL_PUBLISH_V3: 'valor_vc_publish_request_v3',
    VALOR_TERMINAL_CANCEL: 'valor_vc_cancel_request_v1',
    VALOR_TRANSACTION_DETAILS: 'valor_txnfetch_request_v1',
    VALOR_TRANSACTION_LIST: 'valor_transaction_list_request_v1',
    VALOR_TRANSACTION_LIST_V2: 'valor_transaction_list_request_v2',
    VALOR_TRANSACTION_STATUS: 'valor_transaction_status_request_v1',
    VALOR_TRANSACTION_STATUS_V2: 'valor_transaction_status_request_v2',
    VALOR_VOID: 'valor_void_request_v1',
    VALOR_VOID_V2: 'valor_void_request_v2',
} as const;

export interface ValorBinLookupEvidenceV1 {
    readonly bin_lookup_evidence_version: 1;
    readonly bin_lookup_backend: 'VALOR';
    readonly pan_prefix: string;
    readonly card_type: CardType;
    readonly provider_response: JsonValue;
}

export interface BinLookupApiEvidenceV1 {
    readonly bin_lookup_evidence_version: 1;
    readonly bin_lookup_backend: 'BINLOOKUPAPI';
    readonly pan_prefix: string;
    readonly card_type: CardType;
    readonly provider_response: JsonValue;
}

// Discriminator: bin_lookup_backend
export type BinLookupEvidenceV1 = ValorBinLookupEvidenceV1 | BinLookupApiEvidenceV1;

export type BinLookupGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.BIN_LOOKUP,
    BinLookupEvidenceV1
>;

export type GatewayLifecycleRequestType =
    (typeof GatewayLifecycleRequestType)[keyof typeof GatewayLifecycleRequestType];

export interface ValorAdditionalPrompt {
    readonly PROMPT_NAME: string;
    readonly PROMPT_VALUE: string;
}

export interface ValorTerminalPublishPayloadV1 {
    readonly TRAN_MODE: string;
    readonly TRAN_CODE: string;
    readonly AMOUNT: string;
    readonly REQ_TXN_ID: string;
    readonly INVOICENUMBER?: string;
    readonly VAS_LABEL1?: string;
    readonly VAS_DATA1?: string;
    readonly VAS_LABEL2?: string;
    readonly VAS_DATA2?: string;
}

export interface ValorTerminalPublishPayloadV2 extends ValorTerminalPublishPayloadV1 {
    readonly TIP_ENTRY?: '1';
}

export interface ValorTerminalPublishGatewayRequestV1 {
    readonly txn_type: 'vc_publish';
    readonly channel_id: string;
    readonly version: string;
    readonly payload: ValorTerminalPublishPayloadV1;
}

export interface ValorTerminalPublishGatewayRequestV2 {
    readonly txn_type: 'vc_publish';
    readonly channel_id: string;
    readonly version: string;
    readonly payload: ValorTerminalPublishPayloadV2;
}

export interface ValorCardTokenSaleGatewayRequest {
    readonly txn_type: 'sale';
    readonly amount: string;
    readonly surchargeIndicator: 0 | 1;
    readonly surchargeAmount: LosslessNumber;
    readonly token?: string;
    readonly invoicenumber: string;
    readonly cardholdername: string;
    readonly address?: string;
    readonly city?: string;
    readonly orderdescription?: string;
    readonly shipping_country?: string;
    readonly zip?: string;
    readonly additional_prompts?: readonly ValorAdditionalPrompt[];
}

export interface ValorVoidGatewayRequest {
    readonly txn_type: 'void';
    readonly ref_txn_id: string;
    readonly surchargeindicator: 1;
    readonly amount?: string;
}

export interface ValorRefundPrompt {
    readonly name: string;
    readonly value: string;
}

export interface ValorRefundGatewayRequest {
    readonly txn_type: 'refund';
    readonly amount: LosslessNumber;
    readonly surchargeIndicator: 0 | 1;
    readonly ref_txn_id: string;
    readonly sale_refund: '1';
    readonly merchantTransactionId: string;
    readonly additionalPrompts?: readonly ValorRefundPrompt[];
    readonly rrn?: string;
    readonly auth_code?: string;
    readonly child_epi?: string;
}

export interface ValorTransactionStatusGatewayRequest {
    readonly REQ_TXN_ID?: string;
    readonly TXN_ID?: string;
    readonly channel_id?: string;
}

export interface ValorTransactionListGatewayRequest {
    readonly date_filter?: string;
    readonly amount?: string;
    readonly ADDNL_VAL_1?: string;
}

export interface ValorReconciliationGatewayRequest {
    readonly gateway_txn_id: string;
    readonly merchant_transaction_id?: string;
    readonly amount?: string;
}

export interface AchPaymentGatewayRequest {
    readonly txn_type: 'ach';
    readonly account_id: string;
}

export interface IntegrationReconciliationGatewayRequest {
    readonly source: string;
}

export interface TerminalSaleDispatchTransactionDetailsSnapshot {
    readonly amount: LosslessNumber;
    readonly currency: string;
    readonly ipw_system_id: number;
    readonly transaction_details_type_id: number;
    readonly transaction_details_type_version: number;
    readonly [key: string]: JsonValue | undefined;
}

export interface TerminalSaleDispatchSourceDocumentKeySnapshot {
    readonly transaction_details_type_id: number;
    readonly transaction_details_type_version: number;
    readonly [key: string]: JsonValue | undefined;
}

export interface TerminalSaleDispatchGatewayRequest {
    readonly amount: string;
    readonly merchant_transaction_id: string;
    readonly source_document_key: TerminalSaleDispatchSourceDocumentKeySnapshot;
    readonly source_document_name: string;
    readonly source_document_reference: string;
    readonly transaction_details: TerminalSaleDispatchTransactionDetailsSnapshot;
}

export type GatewayLifecycleRequestVariant<
    TRequestType extends GatewayLifecycleRequestType,
    TGatewayRequest,
> = {
    readonly gateway_request_type: TRequestType;
    readonly gateway_request: TGatewayRequest;
};

export type ValorTerminalPublishGatewayLifecycleRequestV1 = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V1,
    ValorTerminalPublishGatewayRequestV1
>;

export type ValorTerminalPublishGatewayLifecycleRequestV2 = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V2,
    ValorTerminalPublishGatewayRequestV2
>;

export type ValorCardTokenSaleGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE,
    ValorCardTokenSaleGatewayRequest
>;

export type ValorVoidGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.VALOR_VOID,
    ValorVoidGatewayRequest
>;

export type ValorRefundGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.VALOR_REFUND,
    ValorRefundGatewayRequest
>;

export type ValorTransactionStatusGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_STATUS,
    ValorTransactionStatusGatewayRequest
>;

export type ValorTransactionListGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_LIST,
    ValorTransactionListGatewayRequest
>;

export type ValorReconciliationGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.VALOR_RECONCILIATION,
    ValorReconciliationGatewayRequest
>;

export type CardPointeCnpTokenSaleGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_CNP_TOKEN_SALE,
    CardPointeCnpTokenSaleRequestDetails
>;

export type CardPointeTerminalSaleGatewayLifecycleRequestV1 = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE_V1,
    StoredCardPointeTerminalSaleRequestLifecyclePayloadV1
>;

export type CardPointeTerminalSaleGatewayLifecycleRequestV2 = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE_V2,
    CardPointeTerminalSaleRequestLifecyclePayloadV2
>;

export type CardPointeLifecyclePreflightGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_PREFLIGHT,
    StoredCardPointeLifecyclePreflightRequestPayload
>;

export type CardPointeLifecycleFinalizationGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION,
    CardPointeLifecycleFinalizationRequestPayload
>;

export type CardPointeTerminalTokenizationGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_TOKENIZATION,
    CardPointeTerminalTokenizationRequestDetails
>;

export type CardPointeTransactionStatusGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TRANSACTION_STATUS,
    CardPointeTransactionStatusRequestDetails
>;

export type CardPointeInquireGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_INQUIRE,
    CardPointeTransactionDetailsRequestDetails
>;

export type CardPointeVoidGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_VOID,
    CardPointeVoidRequestDetails
>;

export type CardPointeRefundGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_REFUND,
    CardPointeRefundRequestDetails
>;

export type CardPointeReconciliationGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_RECONCILIATION,
    CardPointeReconciliationLookupRequestDetails
>;

export type CardPointeStoredCardSaleGatewayLifecycleRequestV1 = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE_V1,
    StoredCardPointeStoredCardSaleRequestDetails
>;

export type CardPointeStoredCardSaleGatewayLifecycleRequestV2 = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE_V2,
    CardPointeCnpTokenSaleRequestDetails
>;

export type CloverCnpTokenSaleGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE,
    CloverCnpTokenSaleRequestDetails
>;

export type CloverTerminalSaleGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE,
    CloverTerminalSaleRequestLifecyclePayload
>;

export type CloverLifecyclePreflightGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT,
    CloverLifecyclePreflightRequestPayload
>;

export type CloverLifecycleFinalizationGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION,
    CloverLifecycleFinalizationRequestPayload
>;

export type CloverTerminalTokenizationGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION,
    CloverTerminalTokenizationRequestDetails
>;

export type CloverTransactionStatusGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS,
    CloverTransactionStatusRequestDetails
>;

export type CloverVoidGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_VOID,
    CloverVoidRequestDetails
>;

export type CloverRefundGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_REFUND,
    CloverRefundRequestDetails
>;

export type CloverReconciliationGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_RECONCILIATION,
    CloverReconciliationLookupRequestDetails
>;

export type CloverStoredCardSaleGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE,
    CloverStoredCardSaleRequestDetails
>;

export type CloverRawGatewayLifecycleRequestType =
    | typeof GatewayLifecycleRequestType.CLOVER_BROWSER_TOKENIZATION
    | typeof GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE_V2
    | typeof GatewayLifecycleRequestType.CLOVER_RECONCILIATION_V2
    | typeof GatewayLifecycleRequestType.CLOVER_REFUND_V2
    | typeof GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE_V2
    | typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_CANCEL
    | typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE_V2
    | typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION_V2
    | typeof GatewayLifecycleRequestType.CLOVER_TRANSACTION_DETAILS
    | typeof GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS_V2
    | typeof GatewayLifecycleRequestType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL
    | typeof GatewayLifecycleRequestType.CLOVER_VOID_V2;

export interface ProviderGatewayRequestSnapshot extends JsonObject {}

export type CardPointeRawGatewayLifecycleRequestType =
    | typeof GatewayLifecycleRequestType.CARDPOINTE_CNP_TOKEN_SALE_V2
    | typeof GatewayLifecycleRequestType.CARDPOINTE_FUNDING_V2
    | typeof GatewayLifecycleRequestType.CARDPOINTE_INQUIRE_V2
    | typeof GatewayLifecycleRequestType.CARDPOINTE_REFUND_V2
    | typeof GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE_V3
    | typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_CANCEL_V2
    | typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE_V3
    | typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_TOKENIZATION_V2
    | typeof GatewayLifecycleRequestType.CARDPOINTE_TRANSACTION_STATUS_V2
    | typeof GatewayLifecycleRequestType.CARDPOINTE_VOID_V2;

export type CardPointeRawGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    CardPointeRawGatewayLifecycleRequestType,
    ProviderGatewayRequestSnapshot
>;

export type CloverRawGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    CloverRawGatewayLifecycleRequestType,
    ProviderGatewayRequestSnapshot
>;

export type ValorRawGatewayLifecycleRequestType =
    | typeof GatewayLifecycleRequestType.VALOR_BROWSER_TOKENIZATION
    | typeof GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE_V2
    | typeof GatewayLifecycleRequestType.VALOR_RECONCILIATION_V2
    | typeof GatewayLifecycleRequestType.VALOR_REFUND_V2
    | typeof GatewayLifecycleRequestType.VALOR_TERMINAL_CANCEL
    | typeof GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V3
    | typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_DETAILS
    | typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_LIST_V2
    | typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_STATUS_V2
    | typeof GatewayLifecycleRequestType.VALOR_VOID_V2;

export type ValorRawGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    ValorRawGatewayLifecycleRequestType,
    ProviderGatewayRequestSnapshot
>;

export type AchPaymentGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.ACH_PAYMENT,
    AchPaymentGatewayRequest
>;

export type IntegrationReconciliationGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.INTEGRATION_RECONCILIATION,
    IntegrationReconciliationGatewayRequest
>;

export type TerminalSaleDispatchGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.TERMINAL_SALE_DISPATCH,
    TerminalSaleDispatchGatewayRequest
>;

// Discriminator: gateway_request_type
export type GatewayLifecycleRequest =
    | BinLookupGatewayLifecycleRequest
    | AchPaymentGatewayLifecycleRequest
    | CardPointeCnpTokenSaleGatewayLifecycleRequest
    | CardPointeInquireGatewayLifecycleRequest
    | CardPointeLifecycleFinalizationGatewayLifecycleRequest
    | CardPointeLifecyclePreflightGatewayLifecycleRequest
    | CardPointeReconciliationGatewayLifecycleRequest
    | CardPointeRefundGatewayLifecycleRequest
    | CardPointeStoredCardSaleGatewayLifecycleRequestV1
    | CardPointeStoredCardSaleGatewayLifecycleRequestV2
    | CardPointeTerminalSaleGatewayLifecycleRequestV1
    | CardPointeTerminalSaleGatewayLifecycleRequestV2
    | CardPointeTerminalTokenizationGatewayLifecycleRequest
    | CardPointeTransactionStatusGatewayLifecycleRequest
    | CardPointeVoidGatewayLifecycleRequest
    | CardPointeRawGatewayLifecycleRequest
    | CloverCnpTokenSaleGatewayLifecycleRequest
    | CloverLifecycleFinalizationGatewayLifecycleRequest
    | CloverLifecyclePreflightGatewayLifecycleRequest
    | CloverReconciliationGatewayLifecycleRequest
    | CloverRefundGatewayLifecycleRequest
    | CloverStoredCardSaleGatewayLifecycleRequest
    | CloverTerminalSaleGatewayLifecycleRequest
    | CloverTerminalTokenizationGatewayLifecycleRequest
    | CloverTransactionStatusGatewayLifecycleRequest
    | CloverVoidGatewayLifecycleRequest
    | CloverRawGatewayLifecycleRequest
    | IntegrationReconciliationGatewayLifecycleRequest
    | TerminalSaleDispatchGatewayLifecycleRequest
    | ValorCardTokenSaleGatewayLifecycleRequest
    | ValorReconciliationGatewayLifecycleRequest
    | ValorRefundGatewayLifecycleRequest
    | ValorTerminalPublishGatewayLifecycleRequestV1
    | ValorTerminalPublishGatewayLifecycleRequestV2
    | ValorTransactionListGatewayLifecycleRequest
    | ValorTransactionStatusGatewayLifecycleRequest
    | ValorVoidGatewayLifecycleRequest
    | ValorRawGatewayLifecycleRequest;

export type GatewayLifecycleRequestForType<TRequestType extends GatewayLifecycleRequestType> =
    Extract<GatewayLifecycleRequest, { readonly gateway_request_type: TRequestType }>;
